import { useState, useEffect, useRef } from 'react';
import {
  useReadContracts,
  useWriteContract,
  useWaitForTransactionReceipt,
  useChainId,
  useAccount,
} from 'wagmi';
import { parseUnits, formatUnits, type Hex } from 'viem';
import { toast } from 'sonner';
import {
  Coins,
  RefreshCw,
  Upload,
  Download,
  CheckCircle2,
  TreePine,
  Copy,
  Calendar,
  Clock,
  AlertTriangle,
  FileUp,
  Trash2,
  Wallet,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { galactica, galacticaCassiopeia } from '@/shared/config';
import { useContractSettings } from '@/shared/hooks/useContractSettings';
import { rewardDistributorAbi } from '@/shared/config/abis';
import { orpc } from '@/shared/api/client';
import { Link } from 'react-router';
import { buildMerkleTree, type MerkleProof } from '@/shared/lib/merkle-tree';
import { Instructions, Step } from '@/shared/ui/instructions';
import {
  calculateVestedAmount,
  calculateToSync,
  getVestingProgress,
  getTimeRemaining,
  formatAmount,
  parseAmount,
} from '../lib/vesting';

interface VestingUser {
  id: number;
  walletAddress: string;
  totalAmount: string;
  distributedAmount: string;
  vestedAmount: bigint;
  toSync: bigint;
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  isLoading,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-[#807f7e]">{title}</CardTitle>
        <Icon className="w-5 h-5 text-[#807f7e]" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-32" />
        ) : (
          <>
            <p className="text-2xl font-bold text-[#2b2b2b]">{value}</p>
            {subtitle && <p className="text-xs text-[#807f7e] mt-1">{subtitle}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function GnetDistributionPage() {
  const chainId = useChainId();
  const { address: walletAddress, isConnected } = useAccount();
  const currentChain = chainId === galactica.id ? galactica : galacticaCassiopeia;

  const [vestings, setVestings] = useState<VestingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<{
    totalUsers: number;
    totalAmount: string;
    totalDistributed: string;
  } | null>(null);

  // CSV import state
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Merkle tree state
  const [isGenerating, setIsGenerating] = useState(false);
  const [merkleRoot, setMerkleRoot] = useState<Hex | null>(null);
  const [merkleProofs, setMerkleProofs] = useState<MerkleProof[]>([]);

  // Get contract settings from database
  const { settings: contractSettings, vestingStart, vestingEnd } = useContractSettings();
  const rewardDistributorAddress = contractSettings.gnetRewardDistributor as `0x${string}` | undefined;

  // Publishing state
  const [isPublishing, setIsPublishing] = useState(false);
  const [isStoringProofs, setIsStoringProofs] = useState(false);
  const [publishedEpoch, setPublishedEpoch] = useState<number | null>(null);

  // Distributions to record after publish
  const [pendingDistributions, setPendingDistributions] = useState<Array<{ wallet: string; amount: string }>>([]);

  // Read current epoch, owner, and reward token from RewardDistributor
  const { data: contractData, refetch: refetchContractData } = useReadContracts({
    contracts: rewardDistributorAddress ? [
      {
        address: rewardDistributorAddress,
        abi: rewardDistributorAbi,
        functionName: 'currentEpoch',
      },
      {
        address: rewardDistributorAddress,
        abi: rewardDistributorAbi,
        functionName: 'rewardMerkleRoot',
      },
      {
        address: rewardDistributorAddress,
        abi: rewardDistributorAbi,
        functionName: 'owner',
      },
      {
        address: rewardDistributorAddress,
        abi: rewardDistributorAbi,
        functionName: 'pendingOwner',
      },
      {
        address: rewardDistributorAddress,
        abi: rewardDistributorAbi,
        functionName: 'rewardToken',
      },
      {
        address: rewardDistributorAddress,
        abi: rewardDistributorAbi,
        functionName: 'totalRewardClaimed',
      },
    ] : [],
    query: { enabled: !!rewardDistributorAddress },
  });

  const currentEpoch = contractData?.[0]?.result as bigint | undefined;
  const currentMerkleRoot = contractData?.[1]?.result as Hex | undefined;
  const contractOwner = contractData?.[2]?.result as `0x${string}` | undefined;
  const pendingOwner = contractData?.[3]?.result as `0x${string}` | undefined;
  // Note: contractData?.[4] is rewardToken - address(0) means native GNET (valid!)
  const totalRewardClaimedFromContract = contractData?.[5]?.result as bigint | undefined;

  // Check if connected wallet is owner
  const isOwner = walletAddress && contractOwner && walletAddress.toLowerCase() === contractOwner.toLowerCase();
  const isPendingOwner = walletAddress && pendingOwner && pendingOwner !== '0x0000000000000000000000000000000000000000' && walletAddress.toLowerCase() === pendingOwner.toLowerCase();

  // Write contract for updating merkle root
  const { writeContract, isPending: isWritePending, data: txHash } = useWriteContract();
  const { isLoading: isTxLoading, isSuccess: isTxSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  // Track which tx we've already handled
  const handledTxRef = useRef<string | null>(null);

  // Handle successful transaction - store proofs and record distributions
  useEffect(() => {
    if (isTxSuccess && txHash && handledTxRef.current !== txHash && merkleRoot && merkleProofs.length > 0) {
      handledTxRef.current = txHash;
      setIsStoringProofs(true);

      const newEpoch = currentEpoch ? Number(currentEpoch) + 1 : 1;

      // Store proofs and record distributions
      Promise.all([
        orpc.gnetVesting.storeProofs({
          chainId,
          epoch: newEpoch,
          root: merkleRoot,
          proofs: merkleProofs.map((p) => ({
            wallet: p.account,
            leafIndex: p.leafIndex,
            amount: p.amount.toString(),
            proof: p.proof as string[],
          })),
        }),
        orpc.gnetVesting.recordDistributions({
          distributions: pendingDistributions,
        }),
      ])
        .then(() => {
          setPublishedEpoch(newEpoch);
          toast.success(`GNET vesting synced! Epoch ${newEpoch}`);
          refetchContractData();
          fetchVestings(); // Refresh data
        })
        .catch((err) => {
          console.error('Failed to store proofs:', err);
          toast.error('Transaction confirmed but failed to store proofs. Export JSON as backup.');
        })
        .finally(() => {
          setIsStoringProofs(false);
          setIsPublishing(false);
          setPendingDistributions([]);
        });
    }
  }, [isTxSuccess, txHash, merkleRoot, merkleProofs, currentEpoch, pendingDistributions, refetchContractData]);

  const isPending = isGenerating || isWritePending || isTxLoading || isStoringProofs;

  // Fetch vestings from API
  const fetchVestings = async () => {
    setLoading(true);
    try {
      const [vestingsResult, statsResult] = await Promise.all([
        orpc.gnetVesting.list({ chainId }),
        orpc.gnetVesting.stats({}),
      ]);

      const now = Date.now();
      const processedVestings: VestingUser[] = vestingsResult.vestings.map((v) => {
        const total = parseAmount(v.totalAmount);
        const distributed = parseAmount(v.distributedAmount);
        const vested = calculateVestedAmount(total, vestingStart, vestingEnd, now);
        const toSync = calculateToSync(total, distributed, vestingStart, vestingEnd, now);

        return {
          id: v.id,
          walletAddress: v.walletAddress,
          totalAmount: v.totalAmount,
          distributedAmount: v.distributedAmount,
          vestedAmount: vested,
          toSync,
        };
      });

      setVestings(processedVestings);
      setStats(statsResult);
    } catch (err) {
      toast.error('Failed to fetch vestings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVestings();
  }, [vestingStart, vestingEnd, chainId]);

  // CSV Import handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');

      // Skip header if present
      const startIdx = lines[0].toLowerCase().includes('wallet') ? 1 : 0;

      const vestingsToImport: Array<{ wallet: string; amount: string }> = [];
      const errors: string[] = [];

      for (let i = startIdx; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const parts = line.split(',').map((p) => p.trim());
        if (parts.length < 2) {
          errors.push(`Line ${i + 1}: Invalid format`);
          continue;
        }

        const [wallet, amount] = parts;

        if (!/^0x[a-fA-F0-9]{40}$/.test(wallet)) {
          errors.push(`Line ${i + 1}: Invalid wallet address`);
          continue;
        }

        if (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
          errors.push(`Line ${i + 1}: Invalid amount`);
          continue;
        }

        vestingsToImport.push({ wallet, amount });
      }

      // Check for duplicate wallet addresses (case-insensitive)
      const walletCounts = new Map<string, number[]>();
      for (let i = 0; i < vestingsToImport.length; i++) {
        const normalizedWallet = vestingsToImport[i].wallet.toLowerCase();
        const lines = walletCounts.get(normalizedWallet) || [];
        lines.push(i + 1);
        walletCounts.set(normalizedWallet, lines);
      }

      const duplicates = Array.from(walletCounts.entries()).filter(([, lines]) => lines.length > 1);
      if (duplicates.length > 0) {
        const [wallet, lines] = duplicates[0];
        errors.push(`Duplicate wallet ${wallet} found on lines: ${lines.join(', ')}`);
      }

      if (errors.length > 0) {
        toast.error(`${errors.length} errors found. First: ${errors[0]}`);
        console.error('Import errors:', errors);
        return;
      }

      if (vestingsToImport.length === 0) {
        toast.error('No valid entries found in CSV');
        return;
      }

      // Import to database
      const result = await orpc.gnetVesting.import({
        vestings: vestingsToImport,
        clearExisting: true,
      });

      toast.success(`Imported ${result.imported} vesting allocations`);
      if (result.errors.length > 0) {
        console.error('Import errors:', result.errors);
      }

      fetchVestings();
    } catch (err) {
      toast.error('Failed to import CSV');
      console.error(err);
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Generate Merkle tree for sync
  const handleGenerateMerkle = async () => {
    const usersToSync = vestings.filter((v) => v.toSync > 0n);

    if (usersToSync.length === 0) {
      toast.error('No users with pending vesting to sync');
      return;
    }

    setIsGenerating(true);

    try {
      // Build leaves for Merkle tree with cumulative amounts
      const leaves = usersToSync.map((user, index) => {
        const currentDistributed = parseAmount(user.distributedAmount);
        const newTotal = currentDistributed + user.toSync;

        return {
          leafIndex: index,
          account: user.walletAddress,
          amount: newTotal,
        };
      });

      // Prepare distributions to record after publish
      const distributions = usersToSync.map((user) => {
        const currentDistributed = parseAmount(user.distributedAmount);
        const newTotal = currentDistributed + user.toSync;
        return {
          wallet: user.walletAddress,
          amount: formatAmount(newTotal, 18),
        };
      });
      setPendingDistributions(distributions);

      // Generate Merkle tree
      const { root, proofs } = buildMerkleTree(leaves);

      setMerkleRoot(root);
      setMerkleProofs(proofs);

      toast.success(`Merkle tree generated for ${usersToSync.length} users`);
    } catch (err) {
      console.error('Failed to generate Merkle tree:', err);
      toast.error('Failed to generate Merkle tree');
    } finally {
      setIsGenerating(false);
    }
  };

  // Publish to contract
  const handlePublish = () => {
    if (!merkleRoot || !rewardDistributorAddress) {
      toast.error('Generate merkle tree and configure GNET RewardDistributor address first');
      return;
    }

    if (!isConnected) {
      toast.error('Connect your wallet first');
      return;
    }

    setIsPublishing(true);
    writeContract({
      address: rewardDistributorAddress,
      abi: rewardDistributorAbi,
      functionName: 'updateRewardMerkleRoot',
      args: [merkleRoot],
    });
  };

  // Download sample CSV
  const downloadSampleCsv = () => {
    const sampleData = `wallet,amount_gnet
0x1234567890123456789012345678901234567890,100000.5
0xabcdefABCDEF12345678901234567890abcdefAB,50000
0x9876543210987654321098765432109876543210,75000.25`;

    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gnet-vesting-sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Copy to clipboard helper
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // Export Merkle data as JSON
  const exportMerkleData = () => {
    if (!merkleRoot || merkleProofs.length === 0) {
      toast.error('Generate Merkle tree first');
      return;
    }

    const data = {
      root: merkleRoot,
      generatedAt: new Date().toISOString(),
      totalUsers: merkleProofs.length,
      proofs: merkleProofs,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gnet-vesting-merkle-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Merkle data exported');
  };

  // Export vesting data as CSV
  const exportVestingCSV = () => {
    const csvContent = [
      ['Wallet Address', 'Total Allocation (GNET)', 'Vested (GNET)', 'Distributed (GNET)', 'To Sync (GNET)'],
      ...vestings.map((v) => [
        v.walletAddress,
        v.totalAmount,
        formatUnits(v.vestedAmount, 18),
        v.distributedAmount,
        formatUnits(v.toSync, 18),
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gnet-vesting-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  // Calculate totals
  const totalToSync = vestings.reduce((sum, v) => sum + v.toSync, 0n);
  const totalVested = vestings.reduce((sum, v) => sum + v.vestedAmount, 0n);
  const usersWithPendingSync = vestings.filter((v) => v.toSync > 0n).length;

  // Total claimed is read directly from the contract
  const totalClaimed = totalRewardClaimedFromContract ?? 0n;

  const vestingProgress = getVestingProgress(vestingStart, vestingEnd);
  const timeRemaining = getTimeRemaining(vestingStart, vestingEnd);
  const vestingNotStarted = Date.now() < vestingStart;
  const vestingStartDate = new Date(vestingStart);
  const vestingEndDate = new Date(vestingEnd);

  const explorerUrl = currentChain.blockExplorers?.default.url || '';

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2b2b2b]">GNET Vesting</h1>
          <p className="text-[#807f7e]">Manage investor GNET vesting distribution</p>
          {rewardDistributorAddress && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-[#807f7e]">Contract:</span>
              <a
                href={`${currentChain.blockExplorers?.default.url}/address/${rewardDistributorAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono text-[#f26f56] hover:underline"
              >
                {rewardDistributorAddress.slice(0, 6)}...{rewardDistributorAddress.slice(-4)}
              </a>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchVestings}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button variant="outline" onClick={downloadSampleCsv}>
            <Download className="w-4 h-4 mr-2" />
            Sample CSV
          </Button>
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isImporting}>
            {isImporting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <FileUp className="w-4 h-4 mr-2" />
                Import CSV
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Connection warning */}
      {!isConnected && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <p className="text-yellow-800">Connect your wallet to sync vesting to the contract</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Owner check warning */}
      {isConnected && rewardDistributorAddress && contractOwner && !isOwner && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-800 font-medium">You are not the contract owner</p>
                  <p className="text-red-700 text-sm mt-1">
                    Only the owner can update the merkle root. Your wallet cannot sign this transaction.
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-[#807f7e]">Contract Owner</span>
                  <a
                    href={`${explorerUrl}/address/${contractOwner}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[#f26f56] hover:underline"
                  >
                    {contractOwner.slice(0, 6)}...{contractOwner.slice(-4)}
                  </a>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#807f7e]">Your Wallet</span>
                  <span className="font-mono">{walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}</span>
                </div>
                {pendingOwner && pendingOwner !== '0x0000000000000000000000000000000000000000' && (
                  <div className="flex justify-between">
                    <span className="text-[#807f7e]">Pending Owner</span>
                    <span className="font-mono text-blue-600">{pendingOwner.slice(0, 6)}...{pendingOwner.slice(-4)}</span>
                  </div>
                )}
              </div>
              <div className="text-sm text-red-700">
                <p className="font-medium">To grant access:</p>
                <ol className="list-decimal list-inside mt-1 space-y-1 text-red-600">
                  <li>Current owner calls <code className="bg-red-100 px-1 rounded">transferOwnership({walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)})</code></li>
                  <li>You click "Accept Ownership" below</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Accept ownership button */}
      {isPendingOwner && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-blue-800 font-medium">Ownership transfer pending</p>
                  <p className="text-blue-700 text-sm">You have been granted ownership. Click to accept.</p>
                </div>
              </div>
              <Button
                onClick={() => {
                  writeContract({
                    address: rewardDistributorAddress!,
                    abi: rewardDistributorAbi,
                    functionName: 'acceptOwnership',
                    args: [],
                  });
                }}
                disabled={isWritePending || isTxLoading}
              >
                {isWritePending || isTxLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  'Accept Ownership'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Instructions title="How to manage GNET vesting">
        <Step number={1} title="Import CSV">
          Upload a CSV file with wallet addresses and total GNET amounts (format: wallet,amount). This is typically done once.
        </Step>
        <Step number={2} title="Review vesting">
          The table shows calculated vested amounts based on the 24-month linear schedule (Jan 2026 - Jan 2028).
        </Step>
        <Step number={3} title="Sync to contract">
          Click "Sync Vesting" to generate a Merkle tree and publish to the contract. Users can then claim their vested tokens.
        </Step>
        <p className="text-xs text-[#807f7e] mt-2">
          Sync daily to keep investor claims up to date with their vested amounts.
        </p>
      </Instructions>

      {/* Vesting Progress */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-[#807f7e] flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Vesting Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-xs text-[#807f7e] mb-1">
                <span>{vestingStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                <span>{vestingEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#f26f56] transition-all duration-300"
                  style={{ width: `${vestingProgress}%` }}
                />
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-[#2b2b2b]">{vestingProgress.toFixed(1)}%</p>
              <p className="text-xs text-[#807f7e] flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {vestingNotStarted
                  ? `Starts in ${Math.ceil((vestingStart - Date.now()) / (1000 * 60 * 60 * 24))} days`
                  : `${timeRemaining.totalDays} days left`
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Investors"
          value={stats?.totalUsers.toLocaleString() ?? '0'}
          icon={Coins}
          isLoading={loading}
        />
        <StatCard
          title="Total Allocation"
          value={stats ? `${formatAmount(parseAmount(stats.totalAmount))} GNET` : '—'}
          icon={Coins}
          isLoading={loading}
        />
        <StatCard
          title="Total Vested"
          value={`${formatAmount(totalVested)} GNET`}
          subtitle={`${vestingProgress.toFixed(1)}% of total`}
          icon={CheckCircle2}
          isLoading={loading}
        />
        <StatCard
          title="Total Claimed"
          value={`${formatAmount(totalClaimed)} GNET`}
          subtitle="by investors"
          icon={Wallet}
          isLoading={loading || totalRewardClaimedFromContract === undefined}
        />
      </div>

      {/* Sync Panel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TreePine className="w-5 h-5" />
                Vesting Sync
              </CardTitle>
              <CardDescription className="flex flex-col gap-1">
                <span>Sync vested amounts to the distribution contract</span>
                {usersWithPendingSync > 0 && (
                  <span className="font-medium text-[#f26f56]">
                    {formatAmount(totalToSync)} GNET pending ({usersWithPendingSync} users)
                  </span>
                )}
              </CardDescription>
            </div>
            {usersWithPendingSync > 0 && !merkleRoot && (
              <Button onClick={handleGenerateMerkle} disabled={isPending}>
                {isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <TreePine className="w-4 h-4 mr-2" />
                    Generate Merkle Tree
                  </>
                )}
              </Button>
            )}
          </div>
        </CardHeader>
        {(merkleRoot || usersWithPendingSync === 0) && (
        <CardContent>
          {/* Merkle Root Display */}
          {merkleRoot && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TreePine className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Merkle Tree Generated</span>
                  {publishedEpoch && (
                    <Badge className="bg-green-600 text-white">Published (Epoch {publishedEpoch})</Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={exportMerkleData}>
                    <Download className="w-4 h-4 mr-1" />
                    Export JSON
                  </Button>
                  {rewardDistributorAddress ? (
                    <Button
                      size="sm"
                      onClick={handlePublish}
                      disabled={isPending || !isConnected || publishedEpoch !== null || !isOwner}
                      title={!isOwner ? 'Only the contract owner can sync' : undefined}
                    >
                      {isWritePending || isTxLoading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          {isTxLoading ? 'Confirming...' : 'Publishing...'}
                        </>
                      ) : isStoringProofs ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          Storing proofs...
                        </>
                      ) : publishedEpoch ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Published
                        </>
                      ) : !isOwner ? (
                        <>
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          Not Owner
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-1" />
                          Sync to Contract
                        </>
                      )}
                    </Button>
                  ) : (
                    <Link to="/settings">
                      <Button size="sm" variant="outline">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        Configure Contract
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-green-700 mb-1">Merkle Root</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm bg-white p-2 rounded border font-mono break-all">
                      {merkleRoot}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(merkleRoot, 'Merkle root')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-green-600">
                  {merkleProofs.length} proofs generated for cumulative vested amounts.
                </p>

                {/* Contract Status */}
                {rewardDistributorAddress && (
                  <div className="mt-3 p-2 bg-white rounded border text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-[#807f7e]">GNET RewardDistributor</span>
                      <a
                        href={`${explorerUrl}/address/${rewardDistributorAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-[#f26f56] hover:underline"
                      >
                        {rewardDistributorAddress.slice(0, 6)}...{rewardDistributorAddress.slice(-4)}
                      </a>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#807f7e]">Current Epoch</span>
                      <span className="font-medium">{currentEpoch !== undefined ? Number(currentEpoch) : '—'}</span>
                    </div>
                  </div>
                )}

                {!rewardDistributorAddress && (
                  <p className="text-xs text-yellow-700 bg-yellow-50 p-2 rounded flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    GNET RewardDistributor not configured. <Link to="/settings" className="underline">Add it in Settings</Link>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* No pending sync message */}
          {usersWithPendingSync === 0 && !merkleRoot && (
            <div className="text-center py-8 text-[#807f7e]">
              {vestingNotStarted ? (
                <>
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-blue-500" />
                  <p>Vesting starts {vestingStartDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  <p className="text-sm mt-1">Nothing has vested yet. Check back after the start date.</p>
                  <Link to="/settings" className="text-sm text-[#f26f56] hover:underline mt-2 inline-block">
                    Configure vesting dates in Settings
                  </Link>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500" />
                  <p>All vested amounts are synced!</p>
                  <p className="text-sm mt-1">Check back later as more tokens vest.</p>
                </>
              )}
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* Vestings table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Investor Vestings</CardTitle>
              <CardDescription>
                {vestings.length} investors with GNET allocations
              </CardDescription>
            </div>
            {vestings.length > 0 && (
              <Button variant="outline" size="sm" onClick={exportVestingCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : vestings.length === 0 ? (
            <div className="text-center py-12 text-[#807f7e]">
              <FileUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No vesting allocations found</p>
              <p className="text-sm mt-1">Import a CSV file to add investor allocations</p>
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Wallet</TableHead>
                    <TableHead className="text-right">Total Allocation</TableHead>
                    <TableHead className="text-right">Vested</TableHead>
                    <TableHead className="text-right">Distributed</TableHead>
                    <TableHead className="text-right">To Sync</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vestings.slice(0, 50).map((vesting) => (
                    <TableRow key={vesting.id}>
                      <TableCell className="font-mono text-sm">
                        <a
                          href={`${explorerUrl}/address/${vesting.walletAddress}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-[#f26f56]"
                        >
                          {vesting.walletAddress.slice(0, 6)}...{vesting.walletAddress.slice(-4)}
                        </a>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatAmount(parseAmount(vesting.totalAmount))} GNET
                      </TableCell>
                      <TableCell className="text-right">
                        {formatAmount(vesting.vestedAmount)} GNET
                      </TableCell>
                      <TableCell className="text-right">
                        {formatAmount(parseAmount(vesting.distributedAmount))} GNET
                      </TableCell>
                      <TableCell className="text-right">
                        {vesting.toSync > 0n ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">
                            +{formatAmount(vesting.toSync)} GNET
                          </Badge>
                        ) : (
                          <span className="text-[#807f7e]">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {vestings.length > 50 && (
                <div className="p-3 text-center text-sm text-[#807f7e] border-t">
                  Showing 50 of {vestings.length} investors
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
